/**
 * Events Scroller Module
 * Handles auto-scrolling with manual scroll override for upcoming events
 *
 * Features:
 * - Auto-scroll with configurable speed
 * - Pause on user interaction (scroll, touch)
 * - Resume after inactivity period
 * - Infinite loop repositioning
 * - Touch/drag support for mobile
 * - Respects prefers-reduced-motion
 *
 * @package Advanced Regeneration
 */

export class EventsScroller {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('.events__upcoming-track, .mn-scroll-slider');

    if (!this.track) return;

    // Configuration
    this.config = {
      scrollSpeed: 0.5,         // pixels per frame (60fps = 30px/sec, smooth and visible)
      inactivityDelay: 3000,    // Resume auto-scroll after 3s
    };

    // State
    this.isAutoScrolling = true;
    this.userInteracting = false;
    this.inactivityTimer = null;
    this.animationFrame = null;
    this.scrollPosition = 0;
    this.contentHeight = 0;
    this.containerHeight = 0;
    this.lastScrollTop = 0;

    // Check for reduced motion preference
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.init();
  }

  init() {
    // Calculate dimensions (no cloning needed)
    this.updateDimensions();

    // Reset to beginning
    this.container.scrollTop = 0;
    this.scrollPosition = 0;
    this.lastScrollTop = 0;

    // Bind event listeners
    this.bindEvents();

    // Start auto-scrolling (unless reduced motion)
    if (!this.prefersReducedMotion) {
      this.startAutoScroll();
    }
  }

  updateDimensions() {
    this.containerHeight = this.container.offsetHeight;
    this.contentHeight = this.track.scrollHeight;
  }

  bindEvents() {
    // Mouse wheel event (user scrolling with wheel)
    this.container.addEventListener('wheel', this.handleWheel.bind(this), { passive: true });

    // Touch events (mobile drag)
    this.container.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
    this.container.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: true });
    this.container.addEventListener('touchend', this.handleTouchEnd.bind(this));

    // Scroll event (for tracking only)
    this.container.addEventListener('scroll', this.handleScrollForLoop.bind(this), { passive: true });

    // Resize observer (recalculate on window resize)
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  handleWheel(e) {
    // User is scrolling with mouse wheel - pause auto-scroll
    this.pauseAutoScroll();
    this.resetInactivityTimer();
  }

  handleScrollForLoop() {
    // Update scroll position tracking
    this.scrollPosition = this.container.scrollTop;
  }

  handleTouchStart(e) {
    this.userInteracting = true;
    this.pauseAutoScroll();
  }

  handleTouchMove(e) {
    this.userInteracting = true;
    this.checkScrollPosition();
  }

  handleTouchEnd() {
    this.userInteracting = false;
    this.resetInactivityTimer();
  }

  handleResize() {
    this.updateDimensions();
  }

  startAutoScroll() {
    if (this.animationFrame || this.prefersReducedMotion) return;

    this.isAutoScrolling = true;

    const scroll = () => {
      if (!this.isAutoScrolling) return;

      // Check if we've reached the end
      const maxScroll = this.container.scrollHeight - this.container.clientHeight;

      if (this.scrollPosition >= maxScroll) {
        // Reached the end - reset to beginning and continue
        this.scrollPosition = 0;
        this.container.scrollTop = 0;
        this.lastScrollTop = 0;
      } else {
        // Increment scroll position
        this.scrollPosition += this.config.scrollSpeed;
        this.container.scrollTop = this.scrollPosition;
      }

      // Continue animation
      this.animationFrame = requestAnimationFrame(scroll);
    };

    this.animationFrame = requestAnimationFrame(scroll);
  }

  pauseAutoScroll() {
    this.isAutoScrolling = false;
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  resetInactivityTimer() {
    // Clear existing timer
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }

    // Start new timer to resume auto-scroll
    this.inactivityTimer = setTimeout(() => {
      if (!this.userInteracting && !this.prefersReducedMotion) {
        this.startAutoScroll();
      }
    }, this.config.inactivityDelay);
  }

  destroy() {
    // Clean up
    this.pauseAutoScroll();

    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }

    // Remove event listeners
    this.container.removeEventListener('wheel', this.handleWheel);
    this.container.removeEventListener('scroll', this.handleScrollForLoop);
    this.container.removeEventListener('touchstart', this.handleTouchStart);
    this.container.removeEventListener('touchmove', this.handleTouchMove);
    this.container.removeEventListener('touchend', this.handleTouchEnd);
    window.removeEventListener('resize', this.handleResize);
  }
}

/**
 * Initialize all events scrollers on the page
 */
export function initEventsScroller() {
  // Manual events (native HTML)
  const manualScrollers = document.querySelectorAll('.events__upcoming-list');
  manualScrollers.forEach(container => {
    new EventsScroller(container);
  });

  // Widget events (third-party embed)
  const widgetScrollers = document.querySelectorAll('.events__upcoming-widget-wrapper');
  widgetScrollers.forEach(container => {
    // Wait for widget to load before initializing
    setTimeout(() => {
      new EventsScroller(container);
    }, 1500);
  });
}
